import type { FormEventHandler, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CargoProblemWorkspaceTransitionInputSchema,
  type CargoProblemWorkspaceNextAction,
  type CargoProblemWorkspaceTransitionInput,
} from '@magimp/shared';
import {
  Button,
  EditorModal,
  FormRow,
  Select,
  Textarea,
  useEditorGuard,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { CARGO_PROBLEM_OUTCOMES, useCargoProblemLabels } from '../labels.js';
import { useTransitionCargoProblemWorkspace } from '../queries.js';
import { useCargoProblemAttachments } from '../useCargoProblemAttachments.js';
import { CargoProblemAttachmentField } from './CargoProblemAttachmentField.js';

type NoteAction = Extract<CargoProblemWorkspaceNextAction, 'reject' | 'submitReview' | 'evaluate'>;
const TRANSITION_FORM_ID = 'cargo-problem-transition-form';

export function CargoProblemTransitionModal({
  problemId,
  action,
  onClose,
}: {
  problemId: string;
  action: NoteAction;
  onClose: () => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  const showToast = useToastStore((state) => state.show);
  const transition = useTransitionCargoProblemWorkspace();
  const attachments = useCargoProblemAttachments();
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CargoProblemWorkspaceTransitionInput>({
    resolver: zodResolver(CargoProblemWorkspaceTransitionInputSchema),
    mode: 'onTouched',
    defaultValues: {
      action,
      note: '',
      attachments: [],
      ...(action === 'evaluate' ? { outcome: 'RESOLVED' } : {}),
    },
  });
  const dirty = isDirty || attachments.items.length > 0;
  const busy = transition.isPending || attachments.busy;
  const title =
    action === 'reject'
      ? t('noteTitles.reject')
      : action === 'submitReview'
        ? t('noteTitles.submitReview')
        : t('noteTitles.evaluate');

  async function confirm(values: CargoProblemWorkspaceTransitionInput) {
    try {
      await attachments.commitWithRecovery(problemId, 'workflow', (files) =>
        transition.mutateAsync({
          id: problemId,
          input: CargoProblemWorkspaceTransitionInputSchema.parse({
            ...values,
            attachments: files,
          }),
        }),
      );
      attachments.clear();
      showToast({ tone: 'success', text: t('toasts.transitioned') });
      onClose();
    } catch (error) {
      showToast({ tone: 'error', text: errorMessage(error) });
    }
  }

  return (
    <EditorModal
      open
      title={title}
      dirty={dirty}
      busy={busy}
      onConfirmClose={onClose}
      footer={<TransitionFooter busy={busy} formId={TRANSITION_FORM_ID} />}
    >
      <GuardedTransitionForm
        id={TRANSITION_FORM_ID}
        onSubmit={handleSubmit(confirm)}
        onClose={onClose}
      >
        <fieldset className="cargo-problem-transition-form" disabled={busy}>
          <FormRow
            label={t('noteTitles.label')}
            error={errors.note ? t('validation.noteRequired') : undefined}
          >
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <Textarea
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  ariaLabel={t('noteTitles.label')}
                  rows={5}
                />
              )}
            />
          </FormRow>
          {action === 'evaluate' ? (
            <FormRow
              label={t('detail.outcome')}
              error={errors.outcome ? t('validation.outcomeRequired') : undefined}
            >
              <Controller
                control={control}
                name="outcome"
                render={({ field }) => (
                  <Select
                    value={field.value ?? 'RESOLVED'}
                    onChange={field.onChange}
                    options={CARGO_PROBLEM_OUTCOMES.map((value) => ({
                      value,
                      label: labels.outcome(value),
                    }))}
                  />
                )}
              />
            </FormRow>
          ) : null}
          <CargoProblemAttachmentField
            items={attachments.items}
            busy={busy}
            onAdd={attachments.add}
            onRemove={attachments.remove}
          />
        </fieldset>
      </GuardedTransitionForm>
    </EditorModal>
  );
}

function GuardedTransitionForm({
  id,
  onSubmit,
  onClose,
  children,
}: {
  id: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onClose: () => void;
  children: ReactNode;
}) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <form
      id={id}
      onSubmit={onSubmit}
      onReset={(event) => {
        event.preventDefault();
        confirmDestructive(onClose);
      }}
      noValidate
    >
      {children}
    </form>
  );
}

function TransitionFooter({ busy, formId }: { busy: boolean; formId: string }) {
  const { t } = useTranslation('cargoProblems');
  return (
    <>
      <Button type="reset" form={formId} kind="ghost" disabled={busy}>
        {t('actions.cancel')}
      </Button>
      <Button type="submit" form={formId} kind="primary" disabled={busy}>
        {t('actions.confirm')}
      </Button>
    </>
  );
}
